import { AxiosInstance } from 'axios';
import qs from 'qs';

// Relative, not the package-internal `@app/*` alias: consumers compile this module from source
// and resolve `@app/*` against their own baseUrl, where it points somewhere else entirely
import { CollectionResource } from '../../data/resources';

import {
    CreateTimeTrackingEntryInput,
    StartTimeTrackingSessionInput,
    StopTimeTrackingSessionInput,
    TimeTrackingEntriesFilterInput,
    UpdateTimeTrackingEntryInput,
} from './inputs';
import { TimeTrackingEntryRef } from './refs';
import {
    TimeTrackingEntryResource,
    TimeTrackingSessionHeartbeatResource,
    TimeTrackingSessionResource,
    TimeTrackingSessionStopResource,
} from './resources';

export default class TimeTracking {
    private basePath = '/api/time-tracking';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Get task list
    get = async (
        filter: TimeTrackingEntriesFilterInput
    ): Promise<CollectionResource<TimeTrackingEntryResource, {}>> => {
        const query = qs.stringify({ ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/entry?${query}`);

        return data;
    };

    // Create new time tracking entry
    create = async (input: CreateTimeTrackingEntryInput): Promise<TimeTrackingEntryRef> => {
        const { data } = await this.http.post(`${this.basePath}/entry`, input);

        return data;
    };

    // Get single time entry data
    getById = async (id: ID): Promise<TimeTrackingEntryResource> => {
        const { data } = await this.http.get(`${this.basePath}/entry/${id}`);

        return data;
    };

    // Update time entry
    update = async (id: ID, input: UpdateTimeTrackingEntryInput): Promise<TimeTrackingEntryRef> => {
        const { data } = await this.http.put(`${this.basePath}/entry/${id}`, input);

        return data;
    };

    // Archive time entry
    archive = async (id: ID): Promise<TimeTrackingEntryRef> => {
        const { data } = await this.http.delete(`${this.basePath}/entry/${id}`);

        return data;
    };

    // Restore archived time entry
    restore = async (id: ID): Promise<TimeTrackingEntryRef> => {
        const { data } = await this.http.post(`${this.basePath}/entry/${id}/restore`);

        return data;
    };

    // Start a durable timer session on a sub-ticket. One active session per designer globally,
    // so starting elsewhere is refused with a conflict naming the sub-ticket already running
    startSession = async (input: StartTimeTrackingSessionInput): Promise<TimeTrackingSessionResource> => {
        const { data } = await this.http.post(`${this.basePath}/session`, input);

        return data;
    };

    // Get the caller's open session, or null. This is what restores the timer after a refresh
    getActiveSession = async (): Promise<TimeTrackingSessionResource | null> => {
        const { data } = await this.http.get(`${this.basePath}/session/active`);

        return data;
    };

    // Keep the session alive; posted every 60s while the workspace tab is open
    heartbeatSession = async (id: ID): Promise<TimeTrackingSessionHeartbeatResource> => {
        const { data } = await this.http.post(`${this.basePath}/session/${id}/heartbeat`);

        return data;
    };

    // Stop & Save: finalises the session into a time tracking entry. Idempotent - a second stop
    // returns the same entry id and writes nothing
    stopSession = async (id: ID, input: StopTimeTrackingSessionInput): Promise<TimeTrackingSessionStopResource> => {
        const { data } = await this.http.post(`${this.basePath}/session/${id}/stop`, input);

        return data;
    };
}
